import { useUserPermissions } from '@/user/permissions/use-user-permissions';
import { useUserProducts } from '@/user/products/use-user-products';
import { useCurrentUser } from '@/user/use-current-user.query';
import { useMemo, type ReactElement } from 'react';
import { PageNotFound } from '../page-not-found';
import type { Fallback, Required } from './protected-route.type';

interface ProtectedRouteProps {
  children: ReactElement;
  required: Required;
  fallback?: Fallback;
}

export const ProtectedRoute = ({
  children,
  required,
  fallback,
}: ProtectedRouteProps) => {
  const { isFetched } = useCurrentUser();

  const { hasRequiredPermissions } = useUserPermissions();
  const { hasRequiredProducts } = useUserProducts();

  const hasAccess = useMemo(() => {
    const hasRequiredPermissionsProperty = required?.permissions
      ? hasRequiredPermissions(required.permissions)
      : true;
    const hasRequiredProductsProperty = required?.products
      ? hasRequiredProducts(required.products)
      : true;

    return hasRequiredPermissionsProperty && hasRequiredProductsProperty;
  }, [
    hasRequiredPermissions,
    hasRequiredProducts,
    required.permissions,
    required.products,
  ]);

  if (!hasAccess && isFetched) {
    if (!fallback) return null;

    if (fallback.navigateToNotFound) {
      return <PageNotFound />;
    }

    return null;
  }

  return children;
};
